import { formatHtml, JINJA_INCLUDE_REGEX } from "./previewMisc";

const extractTemplateNames = (arrayContent) => {
  if (!arrayContent) return [];

  try {
    const templateNameRegex = /["']([^"']+)["']/g;
    const templateMatches = Array.from(
      arrayContent.matchAll(templateNameRegex)
    );
    return templateMatches.map((match) => match[1]);
  } catch (error) {
    console.error("Error extracting template names:", error);
    return [];
  }
};

export const extractBlockNames = (fileContent) => {
  if (!fileContent) return [];

  try {
    const blockNamesInContent = [];
    const blockNameMatches = Array.from(
      fileContent.matchAll(JINJA_INCLUDE_REGEX)
    );

    blockNameMatches.forEach((match) => {
      if (match[1]) {
        const templateNames = extractTemplateNames(match[1]);
        blockNamesInContent.push(...templateNames);
      } else if (match[2]) {
        blockNamesInContent.push(match[2]);
      } else if (match[3]) {
        blockNamesInContent.push(match[3]);
      }
    });

    return blockNamesInContent;
  } catch (error) {
    console.error("Error extracting block names:", error);
    return [];
  }
};

const processFragmentReplacement = (
  match,
  fragmentMap,
  processFoundFragment = (fragment, name) => fragment.content,
  processMissingFragment = (originalMatch) => originalMatch
) => {
  const [fullMatch, arrayContent, singleTemplate, variableName] = match;

  if (arrayContent) {
    const templateNames = extractTemplateNames(arrayContent);

    for (const blockName of templateNames) {
      if (fragmentMap[blockName]) {
        return processFoundFragment(
          fragmentMap[blockName],
          blockName,
          true,
          arrayContent
        );
      }
    }

    return processMissingFragment(fullMatch, templateNames.join(", "), true);
  } else if (singleTemplate && fragmentMap[singleTemplate]) {
    return processFoundFragment(fragmentMap[singleTemplate], singleTemplate);
  } else if (variableName && fragmentMap[variableName]) {
    return processFoundFragment(fragmentMap[variableName], variableName);
  }

  return processMissingFragment(fullMatch, singleTemplate || variableName);
};

export const replacePlainTextFragments = (fileContent, fragmentMap) => {
  if (!fileContent) return "";
  if (!fragmentMap) return fileContent;

  try {
    return fileContent.replace(
      JINJA_INCLUDE_REGEX,
      (match, arrayContent, singleTemplate, variableName) => {
        return processFragmentReplacement(
          [match, arrayContent, singleTemplate, variableName],
          fragmentMap
        );
      }
    );
  } catch (error) {
    console.error("Error replacing plain text fragments:", error);
    return fileContent;
  }
};

export const replaceRichTextFragments = (fileContent, fragmentMap) => {
  if (!fileContent) return "";
  if (!fragmentMap) return fileContent;

  try {
    return fileContent.replace(
      JINJA_INCLUDE_REGEX,
      (match, arrayContent, singleTemplate, variableName) => {
        return processFragmentReplacement(
          [match, arrayContent, singleTemplate, variableName],
          fragmentMap,
          (fragment, blockName, isArray = false, originalArray = null) => {
            const displayName = isArray
              ? `${blockName} (from array)`
              : blockName;
            return createFragmentBlockHTML(true, displayName, fragment.content);
          },
          (originalMatch, blockName, isArray = false) => {
            if (isArray) {
              return createFragmentBlockHTML(
                false,
                `Missing Fragments: ${blockName}`,
                originalMatch,
                "No configured fragments match any in this array"
              );
            }
            return createFragmentBlockHTML(false, blockName, originalMatch);
          }
        );
      }
    );
  } catch (error) {
    console.error("Error replacing rich text fragments:", error);
    return fileContent;
  }
};

export const generateFragmentReport = (blockNamesInContent, fragmentMap) => {
  try {
    if (!blockNamesInContent || blockNamesInContent.length === 0) {
      return "<i>No fragments included in this template</i>";
    }

    if (!fragmentMap) {
      return formatHtml.span(
        "error-text",
        "Fragment map is missing or invalid"
      );
    }

    const fragmentItems = blockNamesInContent.map((blockName) => {
      const isConfigured = !!fragmentMap[blockName];

      if (isConfigured) {
        return `${formatHtml.span(
          "success-text",
          `✓ ${blockName}`
        )} - Fragment configured and available`;
      } else {
        return `${formatHtml.span(
          "error-text",
          `✗ ${blockName}`
        )} - <strong>Not configured!</strong> This fragment is referenced but not available`;
      }
    });

    return formatHtml.ul(fragmentItems);
  } catch (error) {
    console.error("Error generating fragment report:", error);
    return formatHtml.span("error-text", "Error generating fragment report");
  }
};

export const buildFragmentMap = async ({
  promptFragments,
  getFragmentVersionsById,
}) => {
  try {
    if (
      !promptFragments ||
      !Array.isArray(promptFragments) ||
      !getFragmentVersionsById
    ) {
      console.warn("Invalid inputs for buildFragmentMap");
      return { fragmentMap: {}, promisedVersions: [] };
    }

    const fragmentVersionCache = new Map();

    const fragmentVersionPromises = promptFragments.map(async (fragment) => {
      try {
        const fragmentId = fragment.prompt_fragment_id;

        if (!fragmentVersionCache.has(fragmentId)) {
          const versions = await getFragmentVersionsById(fragmentId);
          fragmentVersionCache.set(fragmentId, versions);
        }

        const versions = fragmentVersionCache.get(fragmentId);
        const filteredVersion = versions.find(
          (v) => v.version === fragment.version
        );

        if (!filteredVersion) {
          console.warn(
            `Fragment version not found: ${fragmentId}, version: ${fragment.version}`
          );
          return null;
        }

        return {
          ...filteredVersion,
          block_name: fragment.block_name,
        };
      } catch (error) {
        console.error(
          `Error processing fragment ${fragment.prompt_fragment_id}:`,
          error
        );
        return null;
      }
    });

    const promisedVersions = (
      await Promise.all(fragmentVersionPromises)
    ).filter(Boolean);

    const fragmentMap = promisedVersions.reduce((map, fragment) => {
      map[fragment.block_name] = fragment;
      return map;
    }, {});

    return { fragmentMap, promisedVersions };
  } catch (error) {
    console.error("Error building fragment map:", error);
    return { fragmentMap: {}, promisedVersions: [] };
  }
};

const createFragmentBlockHTML = (
  isConfigured,
  blockName,
  content,
  tooltip = null
) => {
  try {
    const status = isConfigured ? "configured" : "not-configured";
    const prefix = isConfigured ? "Fragment" : "Missing Fragment";
    const headerText = `${prefix}: ${blockName}`;
    const tooltipText = tooltip || headerText;

    return `<div class="fragment-block ${status}">
      <div class="fragment-tooltip">${tooltipText}</div>
      ${content}
    </div>`;
  } catch (error) {
    console.error("Error creating fragment HTML:", error);
    return `<div class="fragment-block error">Error rendering fragment</div>`;
  }
};
